import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { systemClock } from '@reos/config';
import { DomainApiError } from '../lib/api-error.js';
import { listOffices, type OfficeDto } from '../lib/offices-api.js';
import {
  commissionPayoutsCsvUrl,
  getCommissionPayouts,
  lockCommissionPeriod,
  payoutCommissionPeriod,
  reopenCommissionPeriod,
  type CommissionPayoutOverviewDto,
  type CommissionPeriodStatus,
  type PayoutsQuery,
} from '../lib/commissions-api.js';
import { moneyShort } from './invoices.js';

/**
 * Krok PROV1 — „Provize": nárok makléřů z UHRAZENÝCH provizních dokladů po
 * měsících, filtr pobočky (POB1), export CSV pro účtárnu a měsíční uzávěrka.
 *
 * JE TO POHLED NA STRÁNCE FAKTURACE (menu „⋯" → Provize), NE NOVÁ POLOŽKA
 * MENU: `nav.spec.ts` je chráněné měřidlo na složení skupiny „Peníze" a na
 * množinu položek menu (`provize` tam je dnes jako „připravuje se"). Stejný
 * důvod, jaký má u sebe zapsaná karta Pobočky (POB1) na záložce Kancelář.
 *
 * Co kdo vidí, rozhoduje SERVER (`payoutScopeOf`) — tady se jen podle
 * `overview.scope` schová filtr pobočky a ovládání uzávěrky, které by makléři
 * stejně skončilo 403.
 */

const LOCALE = 'cs' as const;

function monthValue(date: Date): string {
  return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Rozsah měsíců se vybírá ze SEZNAMU, ne `<input type="month">`: Chromium
 * kreslí nativní datumové pole podle lokalizace OS, takže český účet by
 * v něm viděl americký zápis (hlídač `test/datum-cs-hlidac.spec.ts`,
 * nález UXF5 #44). Nabídka jde od nejnovějšího měsíce dozadu.
 */
const MONTH_CHOICES = 24;

function monthChoices(now: Date): readonly string[] {
  return Array.from({ length: MONTH_CHOICES }, (_, index) => monthValue(new Date(now.getFullYear(), now.getMonth() - index, 1)));
}

/** „září 2026" — měsíc se v tabulce píše slovy, ne jako `2026-09`. */
function monthLabel(month: string): string {
  const [year, index] = month.split('-');
  if (year === undefined || index === undefined) return month;
  return new Date(Number(year), Number(index) - 1, 1).toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });
}

function statusLabel(status: CommissionPeriodStatus): string {
  const key = status === 'open' ? 'statusOpen' : status === 'locked' ? 'statusLocked' : 'statusPaid';
  return t(`webAdmin.provize.${key}` as MessageKey, { locale: LOCALE });
}

export function CommissionPayoutsTab(): React.JSX.Element {
  const now = systemClock.now();
  const months = React.useMemo(() => monthChoices(now), [now]);
  const defaultFrom = monthValue(new Date(now.getFullYear(), now.getMonth() - 5, 1));
  const [monthFrom, setMonthFrom] = React.useState(defaultFrom);
  const [monthTo, setMonthTo] = React.useState(monthValue(now));
  const [officeId, setOfficeId] = React.useState('');
  const [overview, setOverview] = React.useState<CommissionPayoutOverviewDto | null>(null);
  const [offices, setOffices] = React.useState<readonly OfficeDto[]>([]);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  /** Měsíc, u kterého se ptáme na důvod zpětného otevření. */
  const [reopening, setReopening] = React.useState<string | null>(null);
  const [reopenReason, setReopenReason] = React.useState('');

  const query: PayoutsQuery = {
    ...(monthFrom === '' ? {} : { monthFrom }),
    ...(monthTo === '' ? {} : { monthTo }),
    ...(officeId === '' ? {} : { officeId }),
  };

  const reload = React.useCallback(async (next: PayoutsQuery) => {
    setLoadError(null);
    try {
      setOverview(await getCommissionPayouts(next));
    } catch (error) {
      setLoadError(error instanceof DomainApiError ? error.message : t('error.internal', { locale: LOCALE }));
    }
  }, []);

  React.useEffect(() => {
    void reload({
      ...(monthFrom === '' ? {} : { monthFrom }),
      ...(monthTo === '' ? {} : { monthTo }),
      ...(officeId === '' ? {} : { officeId }),
    });
  }, [reload, monthFrom, monthTo, officeId]);

  React.useEffect(() => {
    if (overview?.scope !== 'all') return;
    void listOffices().then(setOffices).catch(() => setOffices([]));
  }, [overview?.scope]);

  async function run(action: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setLoadError(null);
    try {
      await action();
      await reload(query);
    } catch (error) {
      setLoadError(error instanceof DomainApiError ? error.message : t('error.internal', { locale: LOCALE }));
    } finally {
      setBusy(false);
    }
  }

  const canManage = overview?.scope === 'all';
  const rows = overview?.rows ?? [];
  /** Stav uzávěrky měsíce, který je v tabulce vidět — i když řádek ještě nemá nárok. */
  const monthsInTable = [...new Set(rows.map((row) => row.month))];

  return (
    <section className="rh-a2-card" data-testid="commission-payouts">
      <h2 className="rh-a2-card__title">{t('webAdmin.provize.title', { locale: LOCALE })}</h2>
      <p className="rh-a2-sub">{t('webAdmin.provize.subtitle', { locale: LOCALE })}</p>

      {overview?.scope === 'own' && (
        <p className="rh-a2-sub" data-testid="commission-scope-note">{t('webAdmin.provize.scopeOwn', { locale: LOCALE })}</p>
      )}
      {overview?.scope === 'office' && (
        <p className="rh-a2-sub" data-testid="commission-scope-note">{t('webAdmin.provize.scopeOffice', { locale: LOCALE })}</p>
      )}

      {/* Na 390 px se filtry lámou pod sebe (`flex-wrap`), na 1440 px stojí v řádku. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginTop: 12 }}>
        <label className="rh-a2-field">
          {t('webAdmin.provize.filterFrom', { locale: LOCALE })}
          <select value={monthFrom} onChange={(e) => setMonthFrom(e.target.value)} data-testid="commission-month-from">
            {months.map((month) => (
              <option key={month} value={month}>{monthLabel(month)}</option>
            ))}
          </select>
        </label>
        <label className="rh-a2-field">
          {t('webAdmin.provize.filterTo', { locale: LOCALE })}
          <select value={monthTo} onChange={(e) => setMonthTo(e.target.value)} data-testid="commission-month-to">
            {months.map((month) => (
              <option key={month} value={month}>{monthLabel(month)}</option>
            ))}
          </select>
        </label>
        {canManage && offices.length > 0 && (
          <label className="rh-a2-field">
            {t('webAdmin.provize.filterOffice', { locale: LOCALE })}
            <select value={officeId} onChange={(e) => setOfficeId(e.target.value)} data-testid="commission-office-filter">
              <option value="">{t('webAdmin.provize.filterOfficeAll', { locale: LOCALE })}</option>
              {offices.map((office) => (
                <option key={office.id} value={office.id}>{office.name}</option>
              ))}
            </select>
          </label>
        )}
        <a className="rh-a2-btn" href={commissionPayoutsCsvUrl(query)} data-testid="commission-export-csv">
          {t('webAdmin.provize.exportCsv', { locale: LOCALE })}
        </a>
      </div>

      {loadError !== null && <p role="alert" className="rh-a2-error">{loadError}</p>}

      {rows.length === 0 ? (
        <div className="rh-a2-empty" data-testid="commission-payouts-empty">
          <p className="rh-a2-empty__title">{t('webAdmin.provize.empty', { locale: LOCALE })}</p>
        </div>
      ) : (
        <div className="rh-a2-tbl-wrap">
          <table className="rh-a2-tbl" data-testid="commission-payouts-table">
            <thead>
              <tr>
                <th>{t('webAdmin.provize.colMonth', { locale: LOCALE })}</th>
                <th>{t('webAdmin.provize.colRecipient', { locale: LOCALE })}</th>
                <th className="rh-a2-r">{t('webAdmin.provize.colEntitled', { locale: LOCALE })}</th>
                <th className="rh-a2-r">{t('webAdmin.provize.colPaid', { locale: LOCALE })}</th>
                <th className="rh-a2-r">{t('webAdmin.provize.colRemaining', { locale: LOCALE })}</th>
                <th>{t('webAdmin.provize.colPeriod', { locale: LOCALE })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const recipientKey =
                  row.recipientType === 'broker' ? 'recipientBroker' : row.recipientType === 'office' ? 'recipientOffice' : 'recipientAgency';
                return (
                  <tr key={`${row.month}|${row.recipientType}|${row.brokerUserId ?? row.officeId ?? ''}`} data-testid={`commission-row-${row.month}-${row.recipientType}`}>
                    <td data-label={t('webAdmin.provize.colMonth', { locale: LOCALE })}>{monthLabel(row.month)}</td>
                    <td data-label={t('webAdmin.provize.colRecipient', { locale: LOCALE })}>
                      <span className="rh-a2-strong">
                        {row.label ?? t(`webAdmin.provize.${recipientKey}` as MessageKey, { locale: LOCALE })}
                      </span>
                      <span className="rh-a2-sub">{t(`webAdmin.provize.${recipientKey}` as MessageKey, { locale: LOCALE })}</span>
                    </td>
                    <td className="rh-a2-r rh-a2-num" data-label={t('webAdmin.provize.colEntitled', { locale: LOCALE })}>
                      {moneyShort(row.entitledAmount, row.currency)}
                    </td>
                    <td className="rh-a2-r rh-a2-num" data-label={t('webAdmin.provize.colPaid', { locale: LOCALE })}>
                      {moneyShort(row.paidAmount, row.currency)}
                    </td>
                    <td className="rh-a2-r rh-a2-num" data-label={t('webAdmin.provize.colRemaining', { locale: LOCALE })}>
                      {moneyShort(row.remainingAmount, row.currency)}
                    </td>
                    <td data-label={t('webAdmin.provize.colPeriod', { locale: LOCALE })}>{statusLabel(row.periodStatus)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {canManage && monthsInTable.length > 0 && (
        <div data-testid="commission-periods">
          <h3 className="rh-a2-card__title">{t('webAdmin.provize.periodsTitle', { locale: LOCALE })}</h3>
          {monthsInTable.map((month) => {
            const period = overview?.periods.find((p) => p.month === month) ?? null;
            const status: CommissionPeriodStatus = period?.status ?? 'open';
            return (
              <div key={month} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }} data-testid={`commission-period-${month}`}>
                <span className="rh-a2-strong">{monthLabel(month)}</span>
                <span className="rh-a2-sub">{statusLabel(status)}</span>
                {status === 'open' && (
                  <button type="button" className="rh-a2-btn" disabled={busy} onClick={() => void run(() => lockCommissionPeriod(month))} data-testid={`commission-lock-${month}`}>
                    {t('webAdmin.provize.periodLock', { locale: LOCALE })}
                  </button>
                )}
                {status === 'locked' && (
                  <button type="button" className="rh-a2-btn rh-a2-btn--primary" disabled={busy} onClick={() => void run(() => payoutCommissionPeriod(month))} data-testid={`commission-payout-${month}`}>
                    {t('webAdmin.provize.periodPayout', { locale: LOCALE })}
                  </button>
                )}
                {status !== 'open' && (
                  <button
                    type="button"
                    className="rh-a2-btn"
                    disabled={busy}
                    onClick={() => { setReopening(month); setReopenReason(''); }}
                    data-testid={`commission-reopen-${month}`}
                  >
                    {t('webAdmin.provize.periodReopen', { locale: LOCALE })}
                  </button>
                )}
                {period?.reopenReason !== null && period?.reopenReason !== undefined && (
                  <span className="rh-a2-sub">{t('webAdmin.provize.reopenedNote', { locale: LOCALE, values: { reason: period.reopenReason } })}</span>
                )}
                {/*
                  Zpětná změna uzavřeného období je bez DŮVODU nemožná — server
                  vrací 422 a důvod jde do `audit_events`. Proto se tady neptáme
                  na potvrzení, ale na text: prázdné pole tlačítko nepovolí.
                */}
                {reopening === month && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', width: '100%' }}>
                    <label className="rh-a2-field" style={{ flex: '1 1 260px' }}>
                      {t('webAdmin.provize.reopenReasonLabel', { locale: LOCALE })}
                      <input
                        type="text"
                        value={reopenReason}
                        placeholder={t('webAdmin.provize.reopenReasonPlaceholder', { locale: LOCALE })}
                        onChange={(e) => setReopenReason(e.target.value)}
                        data-testid={`commission-reopen-reason-${month}`}
                      />
                    </label>
                    <button
                      type="button"
                      className="rh-a2-btn rh-a2-btn--primary"
                      disabled={busy || reopenReason.trim().length < 3}
                      onClick={() => void run(async () => {
                        await reopenCommissionPeriod(month, reopenReason.trim());
                        setReopening(null);
                      })}
                      data-testid={`commission-reopen-confirm-${month}`}
                    >
                      {t('webAdmin.provize.reopenConfirm', { locale: LOCALE })}
                    </button>
                    <button type="button" className="rh-a2-btn" onClick={() => setReopening(null)} data-testid={`commission-reopen-cancel-${month}`}>
                      {t('webAdmin.provize.reopenCancel', { locale: LOCALE })}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
